/**
 * MindFlow Cosmic Design System
 * 
 * Tüm MindFlow bileşenlerinde kullanılan tasarım sabitleri.
 * P4.3: GPU optimizasyonu için blur/shadow değerleri hafifletildi.
 */

// ========================================
// DEVICE DETECTION (for performance optimization)
// ========================================
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
const isLowPerfDevice = typeof navigator !== 'undefined' && 
  (navigator.hardwareConcurrency <= 4 || navigator.deviceMemory <= 4);

// ========================================
// COLORS
// ========================================
export const COSMIC_COLORS = {
  // Primary
  primary: '#6D00FF',
  primarySoft: '#8E3BFF',
  
  // Accent
  accent: '#00D4FF',
  accentSoft: '#4BE6FF',
  
  // Background
  bgDark: '#050712',
  bgCard: 'rgba(9,11,25,0.85)',
  
  // Border & Text
  borderSubtle: '#262A3F',
  textMain: '#F5F7FF',
  textSoft: '#A2A7C7',
  textMuted: '#6B7280',
  
  // Functional Colors
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  
  // Gradients
  gradientPrimary: 'linear-gradient(135deg, #6D00FF 0%, #00D4FF 100%)',
  gradientCard: 'linear-gradient(135deg, rgba(109, 0, 255, 0.12) 0%, rgba(0, 212, 255, 0.06) 100%)',
  gradientGlow: 'linear-gradient(135deg, rgba(109, 0, 255, 0.3) 0%, rgba(0, 212, 255, 0.15) 100%)',
};

// ========================================
// CARD STYLES (GPU Optimized)
// ========================================
const BLUR_INTENSITY = isMobile || isLowPerfDevice ? 10 : 18;
const SHADOW_INTENSITY = isMobile || isLowPerfDevice ? 0.35 : 0.55;

export const COSMIC_CARD = {
  background: COSMIC_COLORS.bgCard,
  borderRadius: 24,
  border: `1px solid ${COSMIC_COLORS.borderSubtle}`,
  boxShadow: `0 22px 60px rgba(0,0,0,${SHADOW_INTENSITY})`,
  backdropFilter: `blur(${BLUR_INTENSITY}px)`,
  WebkitBackdropFilter: `blur(${BLUR_INTENSITY}px)`,
  // GPU acceleration
  transform: 'translateZ(0)',
  willChange: 'transform',
};

export const COSMIC_CARD_GLOW = {
  ...COSMIC_CARD,
  boxShadow: `0 22px 60px rgba(0,0,0,${SHADOW_INTENSITY}), 0 0 0 1px rgba(0,212,255,0.25)`,
};

// ========================================
// ANIMATION (GPU Optimized)
// ========================================
// Reduced durations for better performance
const DURATION_MULTIPLIER = isMobile || isLowPerfDevice ? 0.7 : 1;

export const COSMIC_ANIMATION = {
  hoverScale: 1.02,
  pressScale: 0.98,
  // Framer Motion için easing array formatında olmalı
  easing: [0.19, 1, 0.22, 1],
  // CSS için string format
  easingCSS: 'cubic-bezier(0.19, 1, 0.22, 1)',
  // Optimized durations
  durationFast: 0.15 * DURATION_MULTIPLIER,
  durationNormal: 0.25 * DURATION_MULTIPLIER,
  durationSlow: 0.35 * DURATION_MULTIPLIER,
  // Modal transitions (faster for better UX)
  modalEnter: 0.25 * DURATION_MULTIPLIER,
  modalExit: 0.2 * DURATION_MULTIPLIER,
  springConfig: { type: 'spring', stiffness: 300, damping: 20 },
  
  // Framer Motion variants
  fadeIn: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.4, ease: [0.19, 1, 0.22, 1] },
  },
  
  scaleIn: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.3, ease: [0.19, 1, 0.22, 1] },
  },
  
  slideIn: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: { duration: 0.4, ease: [0.19, 1, 0.22, 1] },
  },
  
  // Pulse animation for buttons
  pulse: {
    scale: [1, 1.05, 1],
    opacity: [0.5, 0.8, 0.5],
    transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
  },
  
  // Breathing animation
  breathe: {
    scale: [1, 1.2, 1],
    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
  },
};

// ========================================
// LEVEL CONFIGURATIONS
// ========================================
export const LEVEL_CONFIG = {
  1: { 
    name: 'Farkındalık', 
    color: '#8B5CF6', 
    colorSoft: '#A78BFA',
    theme: 'Zihin uyanıyor',
    xpRequired: 0,
    xpToNext: 300,
  },
  2: { 
    name: 'Uyanış', 
    color: '#10B981', 
    colorSoft: '#34D399',
    theme: 'Bilinç genişliyor',
    xpRequired: 300,
    xpToNext: 600,
  },
  3: { 
    name: 'Dönüşüm', 
    color: '#F59E0B', 
    colorSoft: '#FBBF24',
    theme: 'Kalıplar kırılıyor',
    xpRequired: 900,
    xpToNext: 1000,
  },
  4: { 
    name: 'Üst Bilinç', 
    color: '#EC4899', 
    colorSoft: '#F472B6',
    theme: 'İçgörü derinleşiyor',
    xpRequired: 1900,
    xpToNext: 1500,
  },
  5: { 
    name: 'Ustalık', 
    color: '#6D00FF', 
    colorSoft: '#8E3BFF',
    theme: 'Işık yayılıyor',
    xpRequired: 3400,
    xpToNext: null, // Max level
  },
};

// ========================================
// MOOD CONFIGURATIONS
// ========================================
export const MOOD_CONFIG = {
  calm: { emoji: '😌', label: 'Sakin', color: '#6366F1' },
  anxious: { emoji: '😰', label: 'Endişeli', color: '#F97316' },
  focused: { emoji: '🎯', label: 'Odaklı', color: '#10B981' },
  stressed: { emoji: '😤', label: 'Stresli', color: '#EF4444' },
  tired: { emoji: '😴', label: 'Yorgun', color: '#6B7280' },
  energetic: { emoji: '⚡', label: 'Enerjik', color: '#F59E0B' },
};

// ========================================
// TRIGGER CONFIGURATIONS (Shadow Mode)
// ========================================
export const TRIGGER_CONFIG = {
  emotional_eating: { emoji: '🍽️', label: 'Yeme Dürtüsü', color: '#F59E0B' },
  anxiety_spike: { emoji: '😰', label: 'Kaygı', color: '#EC4899' },
  procrastination: { emoji: '⏰', label: 'Erteleme', color: '#8B5CF6' },
  argument: { emoji: '🗣️', label: 'Tartışma', color: '#EF4444' },
  confidence_drop: { emoji: '😔', label: 'Özgüven', color: '#6366F1' },
  overwhelm: { emoji: '🌊', label: 'Bunalmış', color: '#06B6D4' },
};

// ========================================
// TELKIN TEXTS
// ========================================
export const HERO_TELKINS = [
  'Sadece bir adım at, devamını zihnin halleder.',
  'Bu an, değişimin başladığı an.',
  'Bugün, dünden daha bilinçli bir gün.',
  'Her nefes yeni bir başlangıç.',
  'Zihnin hazır, sadece izin ver.',
];

export const getRandomTelkin = () => {
  return HERO_TELKINS[Math.floor(Math.random() * HERO_TELKINS.length)];
};

// ========================================
// CSS HELPERS
// ========================================
export const cosmicGradientText = {
  background: COSMIC_COLORS.gradientPrimary,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export const neonGlow = (color = COSMIC_COLORS.accent) => ({
  boxShadow: `0 0 20px ${color}40, 0 0 40px ${color}20`,
});

export const cosmicBorder = {
  border: `1px solid ${COSMIC_COLORS.borderSubtle}`,
  borderImage: `linear-gradient(135deg, ${COSMIC_COLORS.primary}40, ${COSMIC_COLORS.accent}40) 1`,
};
